import React, { useEffect, useRef, useState } from "react";
import type { Charge, Period } from "../types";
import type { ChargePop } from "../hooks/useCashclock";
import "./TickerDisplay.css";

type Props = {
  period: Period | null;
  charges: Charge[];
  serverOffsetMs: number;
  pops: ChargePop[];
  onPopDone: (key: number) => void;
  led?: boolean;
};

export default function TickerDisplay({
  period,
  charges,
  serverOffsetMs,
  pops,
  onPopDone,
  led = false,
}: Props) {
  const [now, setNow] = useState<number>(Date.now() + serverOffsetMs);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      setNow(Date.now() + serverOffsetMs);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      stopped = true;
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [serverOffsetMs]);

  if (!period) {
    return (
      <div className="ticker-display ticker-empty">
        <div className="ticker-empty-text">
          <span className="dot" />{" "}
          {led ? "no budget" : "set a budget below to start the clock"}
        </div>
      </div>
    );
  }

  const start = new Date(period.start_at).getTime();
  const end = new Date(period.end_at).getTime();
  const totalDuration = Math.max(1, end - start);
  const elapsed = Math.max(0, Math.min(totalDuration, now - start));
  const elapsedFrac = elapsed / totalDuration;

  const spentCents = charges.reduce((s, c) => s + c.amount_cents, 0);
  const incomeCents = period.income_cents;

  // Linear burn-down of remaining budget: ideal-at-now minus what's already been spent.
  const idealRemainingCents = Math.max(0, incomeCents * (1 - elapsedFrac));
  const remainingCents = Math.max(0, idealRemainingCents - spentCents);

  // The "pace" gauge — positive means ahead of pace, negative means behind.
  const idealSpentCents = incomeCents * elapsedFrac;
  const paceCents = idealSpentCents - spentCents; // >0 = under-spent vs schedule
  const pacePct = incomeCents > 0 ? paceCents / incomeCents : 0;

  const remainingFrac = incomeCents > 0 ? remainingCents / incomeCents : 0;
  const color = led
    ? ledColorForFrac(remainingFrac, pacePct)
    : colorForFrac(remainingFrac, pacePct);

  return (
    <div className="ticker-display" style={{ background: color.bg }}>
      <div className="ticker-period-label">
        {period.label} · {formatRange(start, end)}
      </div>

      <div className="ticker-amount-wrap">
        <span className="ticker-amount" style={{ color: color.fg }}>
          {formatMoney(remainingCents)}
        </span>
        <span className="ticker-amount-sub">
          remaining {paceLabel(paceCents)}
        </span>
      </div>

      <div className="ticker-progress">
        <div
          className="ticker-progress-fill"
          style={{
            width: `${remainingFrac * 100}%`,
            background: color.fg,
          }}
        />
      </div>

      <div className="ticker-pops">
        {pops.map((p) => (
          <ChargePopFx key={p.key} pop={p} onDone={onPopDone} />
        ))}
      </div>
    </div>
  );
}

function ChargePopFx({
  pop,
  onDone,
}: {
  pop: ChargePop;
  onDone: (key: number) => void;
}) {
  useEffect(() => {
    const t = setTimeout(() => onDone(pop.key), 2200);
    return () => clearTimeout(t);
  }, [pop.key, onDone]);
  return (
    <div className="charge-pop">
      <span className="charge-pop-amount">
        -{formatMoney(pop.charge.amount_cents)}
      </span>
      {pop.charge.merchant ? (
        <span className="charge-pop-merchant">{pop.charge.merchant}</span>
      ) : null}
    </div>
  );
}

function formatMoney(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRange(start: number, end: number): string {
  const f = (t: number) =>
    new Date(t).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  return `${f(start)} → ${f(end)}`;
}

function paceLabel(paceCents: number): string {
  if (Math.abs(paceCents) < 100) return "· on pace";
  if (paceCents > 0) return `· ${formatMoney(paceCents)} ahead of pace`;
  return `· ${formatMoney(-paceCents)} behind pace`;
}

function colorForFrac(
  remaining: number,
  pacePct: number
): { fg: string; bg: string } {
  // Bias toward pacePct so the color also reacts to how aggressively you're spending.
  const blend = Math.max(0, Math.min(1, remaining * 0.7 + pacePct * 0.6 + 0.15));
  if (blend > 0.66) return { fg: "#48d597", bg: "linear-gradient(180deg,#0e1a14,#0a0e1a)" };
  if (blend > 0.33) return { fg: "#f5c451", bg: "linear-gradient(180deg,#1a160e,#0a0e1a)" };
  return { fg: "#ef6e6e", bg: "linear-gradient(180deg,#1a0e0e,#0a0e1a)" };
}

// Pure-saturation palette for LED rendering — picks a single color band
// instead of blending, since LED matrices typically can't render gradients.
function ledColorForFrac(
  remaining: number,
  pacePct: number
): { fg: string; bg: string } {
  const blend = Math.max(0, Math.min(1, remaining * 0.7 + pacePct * 0.6 + 0.15));
  if (blend > 0.66) return { fg: "#00ff66", bg: "#000" };
  if (blend > 0.33) return { fg: "#ffcc00", bg: "#000" };
  return { fg: "#ff3333", bg: "#000" };
}
